// Encanamento compartilhado das duas visões de Meios de pagamento: dados,
// mutations, filtros, deep-links e o estado dos quatro diálogos.
//
// Existe para que as visões (por método / por white label) sejam só
// agrupamento e renderização. Enquanto isso viveu copiado em dois painéis, um
// bug de encanamento precisava ser corrigido duas vezes — foi exatamente assim
// que a instância duplicada da mutation de padrão passou pelo review: o painel
// observava uma instância e o diálogo disparava outra, então a linha nunca
// travava durante a eleição. Com uma instância só, esse erro deixa de ser
// possível por construção.
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import {
  useActivateProcessorConfigMutation,
  useDeactivateProcessorConfigMutation,
  useProcessorConfigMetadata,
  useProcessorConfigs,
  useSetDefaultProcessorConfigMutation,
} from "@/services/admin"
import type {
  ProcessorConfigItem,
  ProcessorGateway,
  ProcessorPaymentMethod,
} from "@/types/admin-processor-config"

import type { PendingFallbackChange } from "./components/change-fallback-dialog"
import type { PendingProcessorChange } from "./components/change-processor-dialog"
import type { PendingDefaultElection } from "./components/set-default-dialog"
import {
  countExpiringCertificates,
  EMPTY_PANEL_FILTERS,
  filterConfigs,
  isDefaultForMethod,
  isLastActiveOfPair,
  type PanelFilters,
} from "./utils"

export const useProcessorConfigPanel = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * Filtros na URL, não em estado local.
   *
   * As duas abas são a MESMA tela em outra lente, e o Radix desmonta a aba
   * inativa: com estado local, trocar de lente jogava fora busca, processador e
   * status de certificado — o operador que filtrou "Empresa X" e quis ver o
   * mesmo recorte pela outra dimensão recomeçava do zero. Na URL, o recorte
   * sobrevive à troca, ao F5 e ao link mandado para um colega.
   *
   * `?whiteLabel=` segue aceito como semente da busca: é o deep-link que a
   * ficha do white label produz.
   */
  const filters: PanelFilters = useMemo(
    () => ({
      search:
        searchParams.get("search") ??
        searchParams.get("whiteLabel") ??
        "",
      gateway:
        (searchParams.get(
          "gateway"
        ) as PanelFilters["gateway"]) ?? null,
      certificateStatus:
        (searchParams.get(
          "certStatus"
        ) as PanelFilters["certificateStatus"]) ?? null,
      expiringOnly: searchParams.get("expiring") === "true",
    }),
    [searchParams]
  )
  // Config aguardando confirmação de DESATIVAÇÃO.
  const [pendingDeactivate, setPendingDeactivate] =
    useState<ProcessorConfigItem | null>(null)
  // Eleição de padrão aguardando confirmação — nomeia quem ganha e quem perde.
  const [pendingDefault, setPendingDefault] =
    useState<PendingDefaultElection | null>(null)
  // Troca de credencial/contingência.
  const [pendingChange, setPendingChange] =
    useState<PendingProcessorChange | null>(null)
  const [pendingFallback, setPendingFallback] =
    useState<PendingFallbackChange | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // Deep-link vindo da aba de métodos do white label: `method` abre o stepper
  // já com o white label e o método escolhidos.
  const linkedMethod = searchParams.get("method")
  const linkedWhiteLabelId = searchParams.get("whiteLabel")

  // Limpa o deep-link ao fechar: sem isso, fechar o dialog o reabriria no
  // próximo render, e o botão de voltar do navegador ficaria preso.
  const clearDeepLink = () => {
    const next = new URLSearchParams(searchParams)
    // Só `method`: o `?config=` é resolvido pela PÁGINA, num redirect que
    // acontece antes de qualquer painel montar — apagá-lo aqui era herança do
    // painel antigo, que era quem tratava aquele link.
    next.delete("method")
    setSearchParams(next, { replace: true })
  }

  const { data, isLoading, isError, refetch } =
    useProcessorConfigs()
  const { data: metadata } = useProcessorConfigMetadata()

  const activateMutation =
    useActivateProcessorConfigMutation()
  const deactivateMutation =
    useDeactivateProcessorConfigMutation()
  const setDefaultMutation =
    useSetDefaultProcessorConfigMutation()

  // Spring Page: os itens moram em `content`.
  const items = useMemo(
    () => data?.content ?? [],
    [data?.content]
  )

  // Banner conta sobre TODOS os itens carregados (não filtrados).
  const expiringCount = countExpiringCertificates(items)

  const filteredItems = useMemo(
    () => filterConfigs(items, filters),
    [items, filters]
  )

  // Label amigável por gateway (metadata; fallback = enum).
  const gatewayLabels = useMemo(() => {
    const labels = new Map<ProcessorGateway, string>()
    metadata?.forEach((gateway) =>
      labels.set(gateway.gateway, gateway.label)
    )
    return labels
  }, [metadata])

  // Opções do filtro de processor: metadata quando disponível,
  // senão os gateways presentes na lista.
  const gatewayOptions = useMemo(() => {
    if (metadata && metadata.length > 0) {
      return metadata.map(({ gateway, label }) => ({
        gateway,
        label,
      }))
    }
    return Array.from(
      new Set(items.map((item) => item.gateway))
    )
      .sort((a, b) => a.localeCompare(b))
      .map((gateway) => ({ gateway, label: gateway }))
  }, [metadata, items])

  // Switches da linha desabilitados enquanto a mutação está em voo.
  const pendingIds = useMemo(() => {
    const ids = new Set<string>()
    if (
      activateMutation.isPending &&
      activateMutation.variables
    ) {
      ids.add(activateMutation.variables)
    }
    if (
      deactivateMutation.isPending &&
      deactivateMutation.variables
    ) {
      ids.add(deactivateMutation.variables)
    }
    if (
      setDefaultMutation.isPending &&
      setDefaultMutation.variables
    ) {
      ids.add(setDefaultMutation.variables.uuid)
    }
    return ids
  }, [
    activateMutation.isPending,
    activateMutation.variables,
    deactivateMutation.isPending,
    deactivateMutation.variables,
    setDefaultMutation.isPending,
    setDefaultMutation.variables,
  ])

  /**
   * Escreve os filtros na URL, com `replace` — filtrar não é navegação, e o
   * voltar do browser deve sair da tela em vez de desfazer cada tecla digitada.
   *
   * O `?whiteLabel=` some assim que o usuário mexe nos filtros: ele é semente
   * da busca, e mantê-lo ressuscitaria o recorte que a pessoa acabou de tirar.
   */
  const handleFiltersChange = (next: PanelFilters) => {
    const params = new URLSearchParams(searchParams)
    params.delete("whiteLabel")

    const write = (key: string, value: string | null) => {
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    }

    write("search", next.search.trim() || null)
    write("gateway", next.gateway)
    write("certStatus", next.certificateStatus)
    write("expiring", next.expiringOnly ? "true" : null)

    setSearchParams(params, { replace: true })
  }

  const clearFilters = () =>
    handleFiltersChange({ ...EMPTY_PANEL_FILTERS })

  const showExpiringOnly = () =>
    handleFiltersChange({ ...filters, expiringOnly: true })

  // Nome exibível da config nos dialogs/toasts: a credencial quando resolvida,
  // senão o label amigável do gateway.
  const displayNameOf = (item: ProcessorConfigItem) =>
    item.processor?.name ??
    gatewayLabels.get(item.gateway) ??
    item.gateway

  // Método traduzido, nunca o enum cru — fallback defensivo porque o backend
  // pode passar a devolver um método que o front ainda não traduz.
  const methodLabelOf = (method: ProcessorPaymentMethod) =>
    t(`pages.adminProcessorConfig.methods.${method}`, {
      defaultValue: method,
    })

  // Ativar dispara direto (otimista); desativar SEMPRE confirma.
  const handleToggleActive = (
    item: ProcessorConfigItem,
    nextActive: boolean
  ) => {
    if (nextActive) {
      activateMutation.mutate(item.id)
      return
    }
    setPendingDeactivate(item)
  }

  const handleConfirmDeactivate = () => {
    if (pendingDeactivate) {
      deactivateMutation.mutate(pendingDeactivate.id)
    }
    setPendingDeactivate(null)
  }

  // Eleger padrão SEMPRE confirma: o dialog nomeia quem ganha e quem perde
  // (o atual padrão do MESMO par, quando houver).
  const handleElectDefault = (
    item: ProcessorConfigItem
  ) => {
    const currentDefault =
      items.find(
        (sibling) =>
          sibling.id !== item.id &&
          sibling.whiteLabelId === item.whiteLabelId &&
          sibling.method === item.method &&
          isDefaultForMethod(sibling)
      ) ?? null
    setPendingDefault({ item, currentDefault })
  }

  // Warning extra quando é a última config ativa do par WL+método.
  const isDeactivatingLastOfPair =
    pendingDeactivate !== null &&
    isLastActiveOfPair(items, pendingDeactivate)

  const hasActiveFilters =
    filters.search !== "" ||
    filters.gateway !== null ||
    filters.certificateStatus !== null ||
    filters.expiringOnly

  return {
    items,
    filteredItems,
    isLoading,
    isError,
    refetch,
    expiringCount,
    filters,
    hasActiveFilters,
    gatewayLabels,
    gatewayOptions,
    pendingIds,
    handleFiltersChange,
    clearFilters,
    showExpiringOnly,
    displayNameOf,
    methodLabelOf,
    handleToggleActive,
    handleElectDefault,
    // Diálogos
    pendingDeactivate,
    setPendingDeactivate,
    isDeactivatingLastOfPair,
    handleConfirmDeactivate,
    pendingDefault,
    setPendingDefault,
    setDefaultMutation,
    pendingChange,
    setPendingChange,
    pendingFallback,
    setPendingFallback,
    isCreateOpen,
    setIsCreateOpen,
    // Deep-link do stepper de criação
    linkedMethod,
    linkedWhiteLabelId,
    clearDeepLink,
  }
}
